import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  WorkflowService,
  WorkflowStatus,
  WorkflowSummary,
} from 'src/app/private/services/workflow.service';
import { PermissionService } from 'src/app/private/services/permission.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { WORKFLOW_MODULES, WorkflowModule } from './workflow-catalog';

/**
 * Workflow Automation list (Automation → Workflow Automation).
 *
 * Lists, creates, activates/pauses and archives workflows. The visual builder
 * that edits a workflow's steps is added in a later step.
 */
@Component({
  selector: 'app-workflow-automation',
  templateUrl: './workflow-automation.component.html',
  styleUrls: ['./workflow-automation.component.scss'],
})
export class WorkflowAutomationComponent implements OnInit, OnDestroy {
  workflows: WorkflowSummary[] = [];
  totalRows = 0;
  currentPage = 0;
  pageSize = 25;
  loading = true;
  loadFailed = false;
  btnLoading = false;
  busyId: number | null = null;

  readonly modules = WORKFLOW_MODULES;
  module: WorkflowModule = 'campaign';

  searchQuery = new FormControl('', { nonNullable: true });
  statusFilter: '' | WorkflowStatus = '';

  readonly statusOptions: { value: '' | WorkflowStatus; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
  ];

  createForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    description: new FormControl('', { nonNullable: true }),
  });

  selected: WorkflowSummary | null = null;
  access = { create: false, update: false, delete: false };

  @ViewChild('createModal') createModal!: TemplateRef<any>;
  @ViewChild('deleteModal') deleteModal!: TemplateRef<any>;

  private destroy$ = new Subject<void>();

  constructor(
    private service: WorkflowService,
    private modal: NgbModal,
    private toast: ToastrService,
    private ls: LocalStorageService,
    private permissions: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.permissions.refresh();
    this.access = this.permissions.getAccessObject('workflow_automation');
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const raw = params.get('module');
      this.module = this.modules.some((item) => item.id === raw) ? (raw as WorkflowModule) : 'campaign';
      this.loadWorkflows(true);
    });
    this.searchQuery.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.loadWorkflows(true));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadWorkflows(resetPage = false): void {
    if (resetPage) this.currentPage = 0;
    this.loading = true;
    this.loadFailed = false;

    this.service
      .list({
        client_id: this.ls.getClientId(),
        page: this.currentPage * this.pageSize,
        limit: this.pageSize,
        search: this.searchQuery.value.trim(),
        module: this.module,
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
      })
      .subscribe({
        next: (res) => {
          this.workflows = res?.data ?? [];
          this.totalRows = res?.count ?? 0;
          this.loading = false;
        },
        error: () => {
          this.workflows = [];
          this.totalRows = 0;
          this.loading = false;
          this.loadFailed = true;
        },
      });
  }

  setModule(module: WorkflowModule): void {
    if (module === this.module) return;
    this.router.navigate([], { queryParams: { module }, queryParamsHandling: 'merge' });
  }

  setStatusFilter(value: '' | WorkflowStatus): void {
    this.statusFilter = value;
    this.loadWorkflows(true);
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadWorkflows();
  }

  // ── Create ────────────────────────────────────────────────────────────────
  openCreate(): void {
    this.createForm.reset({ name: '', description: '' });
    this.modal.open(this.createModal, { centered: true });
  }

  submitCreate(): void {
    if (this.createForm.invalid || this.btnLoading) {
      this.createForm.markAllAsTouched();
      return;
    }
    const { name, description } = this.createForm.getRawValue();
    this.btnLoading = true;
    this.service
      .create({
        client_id: this.ls.getClientId(),
        name: name.trim(),
        description: description.trim(),
        created_by: this.ls.getUserId(),
        settings: { module: this.module },
      })
      .subscribe({
        next: (res) => {
          this.btnLoading = false;
          this.modal.dismissAll();
          const id = res?.data?.id;
          if (id) {
            this.router.navigate(['/main/workflow-automation', id]);
            return;
          }
          this.toast.success('Workflow created as a draft');
          this.loadWorkflows(true);
        },
        error: (err) => {
          this.btnLoading = false;
          this.toast.error(this.errorMessage(err, 'Could not create the workflow'));
        },
      });
  }

  // ── Activate / pause ──────────────────────────────────────────────────────
  openBuilder(w: WorkflowSummary): void {
    this.router.navigate(['/main/workflow-automation', w.id]);
  }

  toggleLabel(w: WorkflowSummary): string {
    return w.status === 'active' ? 'Pause' : 'Activate';
  }

  toggleStatus(w: WorkflowSummary): void {
    if (this.busyId !== null) return;
    const next = w.status === 'active' ? 'paused' : 'active';
    this.busyId = w.id;
    this.service
      .setStatus({
        client_id: this.ls.getClientId(),
        workflow_id: w.id,
        status: next,
        updated_by: this.ls.getUserId(),
      })
      .subscribe({
        next: () => {
          this.busyId = null;
          this.toast.success(next === 'active' ? 'Workflow activated' : 'Workflow paused');
          this.loadWorkflows();
        },
        error: (err) => {
          this.busyId = null;
          // The API returns the first problem in `message` and every problem in `errors`.
          this.toast.error(this.errorMessage(err, 'Could not change the workflow status'));
        },
      });
  }

  // ── Delete (archive) ──────────────────────────────────────────────────────
  openDelete(w: WorkflowSummary): void {
    this.selected = w;
    this.modal.open(this.deleteModal, { size: 'sm', centered: true });
  }

  confirmDelete(): void {
    if (!this.selected || this.btnLoading) return;
    this.btnLoading = true;
    this.service
      .delete({
        client_id: this.ls.getClientId(),
        workflow_id: this.selected.id,
        updated_by: this.ls.getUserId(),
      })
      .subscribe({
        next: () => {
          this.btnLoading = false;
          this.modal.dismissAll();
          this.toast.success('Workflow deleted');
          this.loadWorkflows();
        },
        error: (err) => {
          this.btnLoading = false;
          this.toast.error(this.errorMessage(err, 'Could not delete the workflow'));
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  statusLabel(status: WorkflowStatus): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  triggerLabel(type: string | null): string {
    switch (type) {
      case 'trigger_campaign':
        return 'Campaign selected';
      case 'trigger_schedule':
        return 'Schedule';
      case 'trigger_lead_created':
        return 'Lead created';
      case 'trigger_lead_status':
        return 'Lead status';
      case 'trigger_deal_created':
        return 'Deal created';
      case 'trigger_deal_stage':
        return 'Deal stage';
      case 'trigger_contact_created':
        return 'Contact created';
      case 'trigger_contact_updated':
        return 'Contact updated';
      default:
        return '—';
    }
  }

  get subtitle(): string {
    switch (this.module) {
      case 'lead':
        return 'Start when a lead is created or its status changes. Then update the lead, create a task, notify the owner, or send a message.';
      case 'deal':
        return 'Start when a deal is created or its stage changes. Then create a task, notify the owner, or update the linked lead.';
      case 'contact':
        return 'Start when a contact is created or updated. Then add them to a group, notify the owner, or send a message.';
      default:
        return 'Automate follow-ups for your campaigns across Email and WhatsApp. Update lead status, create tasks, notify owners and more.';
    }
  }

  get emptyHint(): string {
    switch (this.module) {
      case 'lead':
        return 'Create a lead workflow. It starts from the lead, not from a campaign.';
      case 'deal':
        return 'Create a deal workflow. It starts when a deal is created or its stage changes.';
      case 'contact':
        return 'Create a contact workflow. It starts when a contact is created or updated.';
      default:
        return 'Create your first workflow to automate follow-ups for a campaign.';
    }
  }

  trackById(_: number, w: WorkflowSummary): number {
    return w.id;
  }

  moduleIcon(id: WorkflowModule): string {
    switch (id) {
      case 'lead':
        return 'fas fa-user';
      case 'deal':
        return 'fas fa-handshake';
      case 'contact':
        return 'fas fa-user-friends';
      default:
        return 'fas fa-bullhorn';
    }
  }

  rowMark(w: WorkflowSummary): { icon: string; tone: string } {
    const name = (w.name || '').toLowerCase();
    if (name.includes('whatsapp')) return { icon: 'fab fa-whatsapp', tone: 'green' };
    if (name.includes('email')) return { icon: 'fas fa-envelope', tone: 'blue' };
    if (w.trigger_type === 'trigger_schedule') return { icon: 'fas fa-clock', tone: 'blue' };
    if (w.trigger_type === 'trigger_lead_created' || w.trigger_type === 'trigger_lead_status') {
      return { icon: 'fas fa-user', tone: 'blue' };
    }
    if (w.trigger_type === 'trigger_deal_created' || w.trigger_type === 'trigger_deal_stage') {
      return { icon: 'fas fa-handshake', tone: 'purple' };
    }
    if (w.trigger_type === 'trigger_contact_created' || w.trigger_type === 'trigger_contact_updated') {
      return { icon: 'fas fa-address-book', tone: 'green' };
    }
    if (!w.trigger_type) return { icon: 'fas fa-envelope', tone: 'purple' };
    if (name.includes('send')) return { icon: 'fas fa-bullhorn', tone: 'purple' };
    const tones = ['purple', 'green', 'blue'];
    return { icon: 'fas fa-envelope', tone: tones[Number(w.id) % tones.length] };
  }

  get rangeStart(): number {
    if (!this.totalRows) return 0;
    return this.currentPage * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.totalRows, (this.currentPage + 1) * this.pageSize);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalRows / this.pageSize) || 1);
  }

  get pageNumbers(): number[] {
    const total = this.pageCount;
    const current = this.currentPage + 1;
    const end = Math.min(total, Math.max(current + 1, 3));
    const start = Math.max(1, end - 2);
    const pages: number[] = [];
    for (let page = start; page <= end; page++) pages.push(page);
    return pages;
  }

  setPageSize(size: number): void {
    this.pageSize = Number(size);
    this.loadWorkflows(true);
  }

  goToPage(index: number): void {
    if (index < 0 || index >= this.pageCount || index === this.currentPage) return;
    this.currentPage = index;
    this.loadWorkflows();
  }

  private errorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body) return body;
    return body?.message || err?.message || fallback;
  }
}
